/**
 * Run summary shown on the game-over screen and recomputed by the server.
 */
import { savedRatingAfterRun } from '../adaptive/rating.ts';
import { median } from '../math/stats.ts';
import { ratedScoreForRun, ratingAnchor } from '../scoring/rated.ts';
import type { GameState, RunSummary } from '../types.ts';

/**
 * RatedScore of the run so far: ratedScoreForRun over the whole history,
 * anchored at max(referenceRating, peakRating). The engine keeps
 * `GameState.rated` equal to this after every answer (the live, provisional
 * value: a later checkpoint can raise the peak and re-weight earlier answers);
 * on a finished run it is the final score.
 */
export function runRatedScore(state: Pick<GameState, 'history' | 'referenceRating' | 'peakRating'>): number {
  return ratedScoreForRun(state.history, ratingAnchor(state.referenceRating, state.peakRating));
}

/**
 * Summarize a run. Call it on a finished run (phase `gameOver`), where the engine
 * clock is frozen at the moment the run ended, so `durationMs` is identical on
 * the client and in the server's replay. On a live run it summarizes "so far".
 */
export function summarize(state: GameState): RunSummary {
  const { history } = state;
  const correct = history.filter((r) => r.outcome === 'correct').length;
  return {
    rulesVersion: state.rulesVersion,
    seed: state.seed,
    raw: state.raw,
    // Recomputed from the history (not copied from state.rated) so the summary is authoritative.
    rated: runRatedScore(state),
    accuracy: history.length > 0 ? correct / history.length : 0,
    medianResponseMs: median(history.map((r) => r.responseMs)),
    levelStart: state.levelStart,
    levelEnd: state.level,
    levelReached: state.maxLevel,
    layersBroken: state.layersBroken,
    problems: history.length,
    correct,
    bestStreak: state.bestStreak,
    durationMs: state.now - state.startedAt,
    startRating: state.startRating,
    finalRating: state.rating,
    peakRating: state.peakRating,
    savedRating: savedRatingAfterRun(state.rating, state.peakRating),
    checkpoints: state.checkpoints.slice(),
    events: state.events.slice(),
  };
}
